import { PlusIcon, XIcon } from '@phosphor-icons/react';
import { useCallback, useMemo, useState } from 'react';
import { LinkWorkSelector } from '@/components/time-entry/LinkWorkSelector';
import { RetainerChip } from '@/components/time-entry/RetainerChip';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { ModuleSearchResult } from '@/lib/api/search';
import { InfoIcon } from '@phosphor-icons/react';
import { ModuleTypeEnum } from '@scrambl/shared';
import type { TimeEntryLinkedModule } from '@scrambl/shared';

// Types that are valid for linked modules but not in the Prisma ModuleType enum
const EXTENDED_VALID_TYPES = new Set<string>([
  ...Object.values(ModuleTypeEnum),
  'TASK_GROUP',
]);

interface LinkedModulePickerProps {
  label?: string;
  value: TimeEntryLinkedModule[];
  onChange: (value: TimeEntryLinkedModule[]) => void;
  className?: string;
  allowedModuleTypes?: string[];
  mode?: 'rows' | 'picker';
  helperText?: string;
}

export function LinkedModulePicker({
  label = 'Link Work',
  value,
  onChange,
  className,
  allowedModuleTypes,
  mode = 'rows',
  helperText,
}: LinkedModulePickerProps) {
  const resolvedValue = value || [];
  const [extraRows, setExtraRows] = useState(0);
  const allowedTypesSet = useMemo(
    () => new Set((allowedModuleTypes && allowedModuleTypes.length > 0 ? allowedModuleTypes : [...EXTENDED_VALID_TYPES]).map((type) => type.toUpperCase())),
    [allowedModuleTypes],
  );

  const baseRowCount = Math.max(1, resolvedValue.length);
  const totalRows = baseRowCount + extraRows;

  const handleRowSelect = useCallback(
    (index: number, result: ModuleSearchResult | null) => {
      const isExtraRow = index >= baseRowCount;
      if (!result) {
        if (index < resolvedValue.length) {
          const next = resolvedValue.filter((_, idx) => idx !== index);
          onChange(next);
        } else if (isExtraRow && extraRows > 0) {
          setExtraRows((prev) => Math.max(0, prev - 1));
        }
        return;
      }

      // Accept both standard ModuleType values and TASK_GROUP
      if (!EXTENDED_VALID_TYPES.has(result.type)) {
        return;
      }
      if (!allowedTypesSet.has(result.type)) {
        return;
      }

      const nextItem: TimeEntryLinkedModule = {
        // Cast needed: TASK_GROUP is valid for linkedModules JSON but not in ModuleTypeEnum
        moduleType: result.type as TimeEntryLinkedModule['moduleType'],
        moduleId: result.id,
        moduleName: result.name ?? null,
      };

      const duplicateIndex = resolvedValue.findIndex(
        (item) => item.moduleType === nextItem.moduleType && item.moduleId === nextItem.moduleId,
      );
      if (duplicateIndex !== -1 && duplicateIndex !== index) {
        return;
      }

      if (index < resolvedValue.length) {
        const next = resolvedValue.map((item, idx) => (idx === index ? nextItem : item));
        onChange(next);
        return;
      }

      onChange([...resolvedValue, nextItem]);
      if (isExtraRow && extraRows > 0) {
        setExtraRows((prev) => Math.max(0, prev - 1));
      }
    },
    [allowedTypesSet, baseRowCount, extraRows, onChange, resolvedValue],
  );

  const handleRemoveEmptyRow = () => {
    setExtraRows((prev) => Math.max(0, prev - 1));
  };

  const handlePickerSelect = useCallback(
    (result: ModuleSearchResult | null) => {
      if (!result) {
        return;
      }

      if (!EXTENDED_VALID_TYPES.has(result.type)) {
        return;
      }

      if (!allowedTypesSet.has(result.type)) {
        return;
      }

      const nextItem: TimeEntryLinkedModule = {
        moduleType: result.type as TimeEntryLinkedModule['moduleType'],
        moduleId: result.id,
        moduleName: result.name ?? null,
      };

      const exists = resolvedValue.some(
        item =>
          item.moduleType === nextItem.moduleType &&
          item.moduleId === nextItem.moduleId,
      );

      if (exists) {
        return;
      }

      onChange([...resolvedValue, nextItem]);
    },
    [allowedTypesSet, onChange, resolvedValue],
  );

  if (mode === 'picker') {
    return (
      <div className={cn('space-y-2', className)}>
        {(label || helperText) && (
          <div
            className={cn(
              'flex items-center gap-2',
              !label && helperText ? 'justify-end' : undefined,
            )}
          >
            {label ? (
              <div className="text-sm font-semibold">{label}</div>
            ) : null}
            {helperText ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex h-5 w-5 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                    aria-label="Linked item tracking info"
                  >
                    <InfoIcon className="h-3.5 w-3.5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-[220px]">
                  {helperText}
                </TooltipContent>
              </Tooltip>
            ) : null}
          </div>
        )}

        <LinkWorkSelector
          className="w-full max-w-full"
          value={null}
          onSelect={handlePickerSelect}
          allowedModuleTypes={Array.from(allowedTypesSet)}
          defaultModule="ALL"
          portalToBody={false}
          closeOnScroll={false}
        />
      </div>
    );
  }

  return (
    <div className={cn('space-y-2', className)}>
      {Array.from({ length: totalRows }).map((_, index) => {
        const rowValue = index < resolvedValue.length ? resolvedValue[index] : null;
        const isExtraRow = index >= baseRowCount;

        return (
          <div key={rowValue ? `${rowValue.moduleType}-${rowValue.moduleId}` : `module-row-${index}`} className="space-y-1">
            <div className="flex items-start gap-2">
              <LinkWorkSelector
                label={index === 0 ? label : undefined}
                className="w-full max-w-full"
                value={
                  rowValue
                    ? {
                        type: rowValue.moduleType,
                        id: rowValue.moduleId,
                        name: rowValue.moduleName ?? undefined,
                      }
                    : null
                }
                onSelect={(result) => handleRowSelect(index, result)}
                allowedModuleTypes={Array.from(allowedTypesSet)}
                defaultModule={!rowValue ? 'ALL' : undefined}
              />
              {isExtraRow && totalRows > 1 && (
                <Button
                  variant="ghost"
                  size="icon"
                  type="button"
                  className="mt-1 h-8 w-8"
                  onClick={handleRemoveEmptyRow}
                  aria-label="Remove linked module row"
                >
                  <XIcon className="h-4 w-4" />
                </Button>
              )}
            </div>
            {rowValue ? (
              <RetainerChip moduleType={rowValue.moduleType} moduleId={rowValue.moduleId} />
            ) : null}
          </div>
        );
      })}
      <Button
        variant="ghost"
        size="sm"
        type="button"
        className="justify-start gap-2 px-0 text-muted-foreground"
        onClick={() => setExtraRows((prev) => prev + 1)}
      >
        <PlusIcon className="h-4 w-4" />
        Add more
      </Button>
    </div>
  );
}
